/**
 * L'ORDRE D'AFFRÈTEMENT — le calcul, 21/10/2026.
 *
 * Le document envoyé au SOUS-TRAITANT. À ne pas confondre avec la confirmation
 * de prise en charge (`confirmation.ts`), qui part vers le CLIENT.
 *
 *
 * CE FICHIER NE RECOPIE RIEN
 * ---------------------------------------------------------------------------
 * Les deux documents partagent la même maquette, donc les mêmes calculs :
 * `charge`, `creneau`, `jourCourt`, `jourLong`, `repere`, `euros` et
 * `centimes` vivent dans `confirmation.ts` et sont importés tels quels. Les
 * recopier ici ferait deux endroits à corriger le jour où l'arithmétique d'un
 * métrage change.
 *
 * `EtapeConfirmation` est réutilisé pour la même raison : les arrêts sont lus
 * par la même requête SQL côté API (`SELECTION_ETAPES`, exportée), donc la
 * forme est identique par construction, pas par coïncidence.
 *
 *
 * CE QUI N'EST PAS ICI, ET QUI NE DOIT PAS Y ARRIVER
 * ---------------------------------------------------------------------------
 * NI `prix_vente`, NI marge, NI indexation gasoil. L'API ne les envoie pas —
 * voir l'avertissement en tête de `apps/api/src/ordre_affretement.ts`. Si un
 * jour un champ de prix de vente apparaît dans ce type, c'est que la fuite a
 * déjà eu lieu côté serveur.
 *
 * `reference_client` et `conditions_paiement` sont encore rendus par l'API et
 * donc encore typés ici, mais la feuille ne les imprime plus depuis le
 * 21/10/2026 — voir la même tête de fichier.
 */

import { appeler, echec, type Resultat } from './api.js';
import type { EtapeConfirmation } from './confirmation.js';

export interface TransporteurOrdre {
  readonly nom: string;
  readonly code: string | null;
  readonly adresse: string | null;
  readonly code_postal: string | null;
  readonly ville: string | null;
  readonly pays: string | null;
  readonly siret: string | null;
  readonly tva: string | null;
  /** Ce que NOUS payons — le commentaire de la colonne le dit en clair. */
  readonly conditions_paiement: string | null;
}

export interface OrdreAffretementLu {
  readonly ordre: {
    readonly numero: string;
    readonly reference_client: string | null;
    readonly marchandise: string | null;
    /** Le matériel COMMANDÉ au sous-traitant, pas le gabarit vendu au client. */
    readonly vehicule: string | null;
    /** La date à laquelle l'ordre a été passé, pas celle du jour. */
    readonly ordonne_le: string;
    readonly ordonne_par: string;
  };
  readonly affretement: {
    readonly prix_achat: string;
    readonly contact_nom: string | null;
    readonly contact_telephone: string | null;
    readonly instructions: string | null;
  };
  readonly transporteur: TransporteurOrdre;
  readonly etapes: readonly EtapeConfirmation[];
}

export async function lireOrdreAffretement(
  jeton: string,
  otId: string,
): Promise<Resultat<OrdreAffretementLu>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/ordre-affretement`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as OrdreAffretementLu };
}
